/**
 * Enhanced Dashboard Layout
 * Glass sidebar with professional icons and animations
 */

import { ReactNode, useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuthStore } from '../stores/authStore'
import Logo from './Logo'
import { 
  MapIcon, 
  GridIcon, 
  SettingsIcon, 
  MenuIcon, 
  CloseIcon,
  LogOutIcon
} from './icons'
import Badge from './ui/Badge'

interface DashboardLayoutProps {
  children: ReactNode
  currentPage: string
  selectedAppId?: string | null
  onNavigate: (page: any) => void
}

export default function DashboardLayout({ children, currentPage, selectedAppId, onNavigate }: DashboardLayoutProps) {
  const { user, logout } = useAuthStore()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const navigation = useMemo(() => [
    { id: 'portfolio', name: 'Portfolio', description: 'Global product health', icon: MapIcon, badge: 'Live' },
    { id: 'apps', name: 'Apps', description: 'Inventory and app scopes', icon: GridIcon, badge: null },
    { id: 'settings', name: 'Settings', description: 'Keys, integrations, account', icon: SettingsIcon, badge: null },
  ], [])

  const activePage = navigation.find(item => item.id === currentPage || (item.id === 'apps' && currentPage.startsWith('app-')))
  const appScopedNavigation = selectedAppId ? [
    { id: 'app-detail', name: 'Overview', description: 'Trends and scorecard', icon: GridIcon, badge: null },
    { id: 'app-live', name: 'Live Pulse', description: 'Stream and active users', icon: MapIcon, badge: 'Live' },
    { id: 'app-breakdowns', name: 'Breakdowns', description: 'Segments and dimensions', icon: GridIcon, badge: null },
    { id: 'app-explore', name: 'Explore', description: 'Events and samples', icon: GridIcon, badge: null },
  ] : []

  useEffect(() => {
    if (!isMobileMenuOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [isMobileMenuOpen])

  const handleNavigate = (page: string) => {
    onNavigate(page)
    setIsMobileMenuOpen(false) // Close menu on mobile after navigation
  }

  return (
    <div className="min-h-[100dvh] flex flex-col md:flex-row bg-[#050507] text-white selection:bg-indigo-400/30">
      {/* Mobile Header */}
      <div className="md:hidden sticky top-0 z-40 glass-bottom-bar flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10">
        <div className="min-w-0">
          <Logo size="sm" />
          <p className="mt-1 text-xs text-gray-500 truncate">{activePage?.name ?? 'AppStats'}</p>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-white transition-colors hover:bg-white/10 active:scale-95"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
          aria-controls="dashboard-navigation"
        >
          {isMobileMenuOpen ? <CloseIcon size="lg" /> : <MenuIcon size="lg" />}
        </button>
      </div>

      {/* Sidebar - Desktop: always visible, Mobile: toggle */}
      <AnimatePresence>
        {(isMobileMenuOpen || typeof window === 'undefined') && (
          <motion.aside
            id="dashboard-navigation"
            className={`
              md:hidden fixed inset-y-0 left-0 z-50 w-[min(88vw,22rem)] glass-panel border-r border-white/10 flex flex-col
            `}
            initial={{ x: '-100%', opacity: 0.8 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0.8 }}
            transition={{ type: 'spring', stiffness: 360, damping: 34 }}
            aria-label="Dashboard navigation"
          >
            <SidebarContent
              navigation={navigation}
              appScopedNavigation={appScopedNavigation}
              currentPage={currentPage}
              user={user}
              onNavigate={handleNavigate}
              onLogout={logout}
            />
          </motion.aside>
        )}
      </AnimatePresence>

      <aside className="hidden md:flex w-72 shrink-0 glass-panel border-r border-white/10 flex-col">
        <SidebarContent
          navigation={navigation}
          appScopedNavigation={appScopedNavigation}
          currentPage={currentPage}
          user={user}
          onNavigate={handleNavigate}
          onLogout={logout}
        />
      </aside>

      {/* Overlay for mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Main content */}
      <main className="relative flex-1 overflow-auto bg-[radial-gradient(circle_at_top_left,rgba(102,126,234,0.16),transparent_34rem),radial-gradient(circle_at_85%_10%,rgba(118,75,162,0.16),transparent_28rem),linear-gradient(135deg,#050507_0%,#0b0b12_45%,#030305_100%)]">
        <div className="pointer-events-none fixed inset-0 opacity-35 [background-image:linear-gradient(rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] [background-size:64px_64px]" />
        <div className="relative min-h-full">
          {children}
        </div>
      </main>
    </div>
  )
}

type NavigationItem = {
  id: string
  name: string
  description: string
  icon: typeof MapIcon
  badge: string | null
}

type SidebarUser = {
  displayName: string | null
  email: string
} | null

function SidebarContent({
  navigation,
  appScopedNavigation,
  currentPage,
  user,
  onNavigate,
  onLogout,
}: {
  navigation: NavigationItem[]
  appScopedNavigation: NavigationItem[]
  currentPage: string
  user: SidebarUser
  onNavigate: (page: string) => void
  onLogout: () => void
}) {
  return (
    <>
            {/* Logo - Hidden on mobile (shown in header) */}
            <motion.div
              className="p-5 md:p-6 border-b border-white/10"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Logo size="md" gradient />
              <div className="mt-5 rounded-3xl border border-white/10 bg-white/[0.04] p-4">
                <p className="text-xs uppercase tracking-[0.22em] text-indigo-200/70">Command Center</p>
                <p className="mt-2 text-sm leading-relaxed text-gray-300">
                  Monitor real-time adoption, product health, and portfolio reach from one surface.
                </p>
              </div>
            </motion.div>

            {/* Navigation */}
            <nav className="flex-1 p-4 overflow-y-auto custom-scrollbar" aria-label="Primary">
              <ul className="space-y-2">
                {navigation.map((item, index) => {
                  const Icon = item.icon
                  const isActive = currentPage === item.id || (item.id === 'apps' && currentPage.startsWith('app-'))
                  
                  return (
                    <motion.li
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <button
                        onClick={() => onNavigate(item.id)}
                        aria-current={isActive ? 'page' : undefined}
                        className={`
                          w-full min-h-[4.25rem] flex items-center gap-3 px-4 py-3 rounded-2xl text-left
                          transition-all duration-300 relative overflow-hidden group
                          ${isActive
                            ? 'bg-gradient-to-r from-[#667EEA] to-[#764BA2] text-white shadow-[0_18px_45px_rgba(102,126,234,0.28)]'
                            : 'text-gray-300 hover:bg-white/[0.07] hover:text-white border border-transparent hover:border-white/10'
                          }
                        `}
                      >
                        {/* Active indicator */}
                        {isActive && (
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-[#667EEA] to-[#764BA2]"
                            layoutId="activeNav"
                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                          />
                        )}
                        
                        <div className="relative z-10 flex items-center gap-3 flex-1 min-w-0">
                          <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${isActive ? 'bg-white/15' : 'bg-white/5 group-hover:bg-white/10'}`}>
                            <Icon size="md" />
                          </span>
                          <span className="min-w-0">
                            <span className="block font-semibold leading-tight">{item.name}</span>
                            <span className={`mt-1 block truncate text-xs ${isActive ? 'text-white/75' : 'text-gray-500'}`}>{item.description}</span>
                          </span>
                        </div>
                        
                        {item.badge && (
                          <span className="relative z-10">
                            <Badge variant={isActive ? 'default' : 'gradient'} size="sm">
                              {item.badge}
                            </Badge>
                          </span>
                        )}
                        
                        {/* Hover effect */}
                        {!isActive && (
                          <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300" />
                        )}
                      </button>
                    </motion.li>
                  )
                })}
              </ul>
              {appScopedNavigation.length > 0 && (
                <div className="mt-6 border-t border-white/10 pt-4">
                  <p className="mb-3 px-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-600">App Scope</p>
                  <ul className="space-y-1.5">
                    {appScopedNavigation.map((item) => {
                      const Icon = item.icon
                      const isActive = currentPage === item.id
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => onNavigate(item.id)}
                            aria-current={isActive ? 'page' : undefined}
                            className={`w-full rounded-2xl px-3 py-2.5 text-left transition-colors ${
                              isActive ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/[0.07] hover:text-white'
                            }`}
                          >
                            <span className="flex items-center gap-2.5">
                              <Icon size="sm" />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold">{item.name}</span>
                                <span className="block truncate text-xs text-gray-600">{item.description}</span>
                              </span>
                              {item.badge && <Badge variant="success" dot size="sm">{item.badge}</Badge>}
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}
            </nav>

            {/* User info */}
            <motion.div
              className="p-4 border-t border-white/10"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="glass-pill p-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#667EEA] to-[#764BA2] rounded-full flex items-center justify-center relative">
                    <span className="text-sm font-semibold">
                      {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
                    </span>
                    {/* Online indicator */}
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-gray-900 rounded-full" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {user?.displayName || 'User'}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>
              
              <button
                onClick={onLogout}
                className="w-full min-h-11 px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-all duration-300 flex items-center gap-2 justify-center group"
              >
                <LogOutIcon size="sm" className="group-hover:rotate-12 transition-transform" />
                <span className="font-medium">Sign Out</span>
              </button>
            </motion.div>
    </>
  )
}
